import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  Trash2, 
  AlertTriangle, 
  Monitor, 
  Smartphone,
  X
} from 'lucide-react';
import { api } from '../../services/api';

/**
 * Admin Edit Scene - Apple Glass Design
 */

const AdminEditScene = () => {
  const { sceneId } = useParams();
  const navigate = useNavigate();
  
  const [scene, setScene] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadScene();
  }, [sceneId]);

  const loadScene = async () => {
    try {
      setLoading(true);
      const data = await api.getPack(sceneId);
      setScene(data);
    } catch (err) {
      setError('Cena não encontrada');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setScene(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updatePack(sceneId, {
        title: scene.title,
        collection: scene.collection,
        tags: scene.tags,
        is_4k: scene.is_4k,
        orientation: scene.orientation
      });
      navigate(`/admin/collection/${encodeURIComponent(scene.collection)}`);
    } catch (err) {
      setError('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await api.deletePack(sceneId);
      localStorage.removeItem('packs_cache_v1');
      navigate('/admin');
    } catch (err) {
      setError('Erro ao deletar');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#FFCC00]/10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#FFCC00]" />
          </div>
          <p className="text-white/40 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error && !scene) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Link to="/admin" className="text-[#FFCC00] hover:underline">Voltar ao Admin</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative">
      {/* Ambient Glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[30%] w-[500px] h-[500px] bg-[#FFCC00]/[0.02] rounded-full blur-[100px]" />
      </div>

      {/* Glass Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/[0.02] border-b border-white/[0.05]">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/admin"
                className="w-10 h-10 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] flex items-center justify-center transition-all"
              >
                <ArrowLeft className="w-5 h-5 text-white/50" />
              </Link>
              <h1 className="text-white font-medium tracking-tight">Editar Cena</h1>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/15 border border-red-500/20 text-red-400 text-sm transition-all"
            >
              <Trash2 className="w-4 h-4" />
              <span>Deletar</span>
            </button>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Preview */}
          {scene.video_url && (
            <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
              <label className="block text-white/60 text-sm mb-3">Preview</label>
              <video 
                src={scene.video_url} 
                controls 
                className="w-full rounded-xl bg-black"
              />
            </div>
          )}

          {/* Title */}
          <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
            <label className="block text-white/60 text-sm mb-3">Título</label>
            <input
              type="text"
              value={scene.title || ''}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
            />
          </div>

          {/* Collection */}
          <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
            <label className="block text-white/60 text-sm mb-3">Coleção</label>
            <input
              type="text"
              value={scene.collection || ''}
              onChange={(e) => handleChange('collection', e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FFCC00]/30 focus:bg-white/[0.05] transition-all"
            />
          </div>

          {/* Options Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Quality */}
            <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
              <label className="block text-white/60 text-sm mb-3">Qualidade</label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleChange('is_4k', true)}
                  className={`flex-1 px-4 py-3 rounded-xl border transition-all ${
                    scene.is_4k 
                      ? 'bg-[#FFCC00]/10 text-[#FFCC00] border-[#FFCC00]/25' 
                      : 'bg-white/[0.03] text-white/50 border-white/[0.06] hover:bg-white/[0.05]'
                  }`}
                >
                  4K
                </button>
                <button
                  onClick={() => handleChange('is_4k', false)}
                  className={`flex-1 px-4 py-3 rounded-xl border transition-all ${
                    !scene.is_4k 
                      ? 'bg-[#FFCC00]/10 text-[#FFCC00] border-[#FFCC00]/25' 
                      : 'bg-white/[0.03] text-white/50 border-white/[0.06] hover:bg-white/[0.05]'
                  }`}
                >
                  HD
                </button>
              </div>
            </div>

            {/* Orientation */}
            <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-6">
              <label className="block text-white/60 text-sm mb-3">Orientação</label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleChange('orientation', 'vertical')}
                  className={`flex-1 px-4 py-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                    scene.orientation === 'vertical'
                      ? 'bg-[#FFCC00]/10 text-[#FFCC00] border-[#FFCC00]/25' 
                      : 'bg-white/[0.03] text-white/50 border-white/[0.06] hover:bg-white/[0.05]'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  <span>9:16</span>
                </button>
                <button
                  onClick={() => handleChange('orientation', 'horizontal')}
                  className={`flex-1 px-4 py-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                    scene.orientation === 'horizontal'
                      ? 'bg-[#FFCC00]/10 text-[#FFCC00] border-[#FFCC00]/25' 
                      : 'bg-white/[0.03] text-white/50 border-white/[0.06] hover:bg-white/[0.05]'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                  <span>16:9</span>
                </button>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && scene && (
            <div className="flex items-center gap-3 p-4 backdrop-blur-xl bg-red-500/10 rounded-2xl border border-red-500/20">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Link
              to={`/admin/collection/${encodeURIComponent(scene.collection)}`}
              className="px-6 py-3.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] text-white/70 transition-all"
            >
              Cancelar
            </Link>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-6 py-3.5 rounded-xl bg-[#FFCC00]/10 hover:bg-[#FFCC00]/15 border border-[#FFCC00]/25 text-[#FFCC00] font-medium transition-all disabled:opacity-40 flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#FFCC00]/10"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              <span>Salvar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={() => setShowDeleteConfirm(false)} />
          
          <div className="relative backdrop-blur-2xl bg-[#0a0a0a]/90 rounded-3xl border border-white/[0.08] p-8 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-medium text-lg">Deletar Cena?</h3>
                <p className="text-white/40 text-sm">Esta ação não pode ser desfeita.</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] text-white/70 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-3 rounded-xl bg-red-500/10 hover:bg-red-500/15 border border-red-500/25 text-red-400 font-medium transition-all disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                <span>Deletar</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEditScene;
